import React, { useEffect, useRef, useState } from 'react'

import { useAppSelector } from '../../app/hooks'
import { selectAnalysisData, selectDomainCss } from '../analysis/AnalysisSlice'
import { Box, Grid, Typography, lighten } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  LineController,
  Tooltip,
  Legend,
  PointElement,
} from 'chart.js';
import { Bar } from 'react-chartjs-2'
import { ErrorBoundary } from 'react-error-boundary';
import ErrorBoundryFallback from './ErrorBoundryFallback';
import { ThemesType } from './MUITheme';
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

export interface GraphyProps {
  mode?: string
  onRenderCompleted?: (image: string, type: string) => void
}


function Graphy(props: GraphyProps) {
  const analysisData = useAppSelector(selectAnalysisData)
  const domainCss = useAppSelector(selectDomainCss)

  const options = (zipdata: any, type: 'average' | 'total') => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: type === 'total' ? 'Total rentals on market' : 'Average rental rates',
        },
      },
      animation: {
        onComplete: (anim: any) => {
          console.log('CHARTJS onComplete', anim.chart)
          if (props.onRenderCompleted) {
            props.onRenderCompleted(anim.chart.toBase64Image(), type)
          }
        }
      },
      onClick: (e: any, item: any) => {
        console.log('CHARTJS onClick', e, item)
      }
    }
  }
  const data: any = (zipdata: any, type: 'average' | 'total', domainCss: ThemesType) => {

    // Get the keys (dates) for all rental data points
    const rentalDataHistory = zipdata?.rentalData?.history
    const allDates = Object.keys(rentalDataHistory);

    // Since we only want to show the last 6 months, get the keys for only the last 6 dates
    const lastSixMonthDates = allDates.slice(-8);

    // Get the market average rents for the last 6 months
    const marketAverageRents = lastSixMonthDates.map(dateKey => rentalDataHistory[dateKey].averageRent);

    // Get the average rents and total rentals for 2-bedroom, 3-bedroom, and 4-bedroom rentals for the last 6 months
    const average2BedroomRents = lastSixMonthDates.map(dateKey => rentalDataHistory[dateKey].detailed.find((item: any) => item.bedrooms === 2)?.averageRent);
    const total2BedroomRentals = lastSixMonthDates.map(dateKey => {
      const tmp = rentalDataHistory[dateKey].detailed.find((item: any) => item.bedrooms === 2)
      return tmp?.totalRentals || tmp?.totalListings
    })

    const average3BedroomRents = lastSixMonthDates.map(dateKey => rentalDataHistory[dateKey].detailed.find((item: any) => item.bedrooms === 3)?.averageRent);
    const total3BedroomRentals = lastSixMonthDates.map(dateKey => {
      const tmp = rentalDataHistory[dateKey].detailed.find((item: any) => item.bedrooms === 3)
      return tmp?.totalRentals || tmp?.totalListings
    })

    const average4BedroomRents = lastSixMonthDates.map(dateKey => rentalDataHistory[dateKey].detailed.find((item: any) => item.bedrooms === 4)?.averageRent);
    const total4BedroomRentals = lastSixMonthDates.map(dateKey => {
      const tmp = rentalDataHistory[dateKey].detailed.find((item: any) => item.bedrooms === 4)
      return tmp?.totalRentals || tmp?.totalListings
    })


    return {
      labels: Object.keys(rentalDataHistory)?.slice(-8),
      datasets: [
        ...(type === 'total' ?
          [{
            yAxisID: 'totalRentals', // Assign the dataset to the secondary y-axis
            label: 'Total 2-Bedroom Rentals',
            data: total2BedroomRentals, // Use the total 2-bedroom rentals data for the Y-axis
            backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.3) : domainCss.chart.light.bgColor, // Set the bar color
            borderColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.2) : domainCss.chart.light.borderColor, // Set the bar border color
            borderWidth: 1, // Set the bar border width
          },
          {
            yAxisID: 'totalRentals', // Assign the dataset to the secondary y-axis
            label: 'Total 3-Bedroom Rentals',
            data: total3BedroomRentals, // Use the total 3-bedroom rentals data for the Y-axis
            backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.6) : domainCss.chart.main.bgColor, // Set the bar color
            borderColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.5) : domainCss.chart.main.borderColor, // Set the bar border color
            borderWidth: 1, // Set the bar border width
          },
          {
            yAxisID: 'totalRentals', // Assign the dataset to the secondary y-axis
            label: 'Total 4-Bedroom Rentals',
            data: total4BedroomRentals, // Use the total 4-bedroom rentals data for the Y-axis
            backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0) : domainCss.chart.dark.bgColor, // Set the bar color
            borderColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.1) : domainCss.chart.dark.borderColor, // Set the bar border color
            borderWidth: 1, // Set the bar border width
          },
          ] : [
            // Line chart datasets for average rents
            {
              type: 'line', // Set line chart as the chart type
              yAxisID: 'averageRents', // Assign the dataset to the primary y-axis
              label: '2-Bedroom Average Rents',
              data: average2BedroomRents, // Use the 2-bedroom average rent data for the Y-axis
              fill: false, // Don't fill the area under the line
              backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.3) : domainCss.chart.light.bgColor, // Set the line color
              borderColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.2) : domainCss.chart.light.borderColor, // Set the line color
              borderWidth: 3, // Set the line thickness
              pointStyle: 'triangle', // Set the point shape
              pointRadius: 7, // Set the point size
            },
            {
              type: 'line', // Set line chart as the chart type
              yAxisID: 'averageRents', // Assign the dataset to the primary y-axis
              label: '3-Bedroom Average Rents',
              data: average3BedroomRents, // Use the 3-bedroom average rent data for the Y-axis
              fill: false, // Don't fill the area under the line
              backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.6) : domainCss.chart.main.bgColor, // Set the line color
              borderColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.5) : domainCss.chart.main.borderColor, // Set the line color
              borderWidth: 3, // Set the line thickness
              pointStyle: 'triangle', // Set the point shape
              pointRadius: 7, // Set the point size
            },
            {
              type: 'line', // Set line chart as the chart type
              yAxisID: 'averageRents', // Assign the dataset to the primary y-axis
              label: '4-Bedroom Average Rents',
              data: average4BedroomRents, // Use the 4-bedroom average rent data for the Y-axis
              fill: false, // Don't fill the area under the line
              backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0) : domainCss.chart.dark.bgColor, // Set the line color
              borderColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.1) : domainCss.chart.dark.borderColor, // Set the line color
              borderWidth: 3, // Set the line thickness
              pointStyle: 'triangle', // Set the point shape
              pointRadius: 7, // Set the point size
            },
          ])
      ]
    }
  }

  return (<>
    {analysisData && <>
      <ErrorBoundary FallbackComponent={ErrorBoundryFallback} >
        <Box sx={{ pageBreakBefore: 'always', pageBreakAfter: 'always' }}>
          <Typography sx={{ flex: '1 1 100%', alignSelf: 'start', textAlign: 'left' }}
            variant="h6" id="tableTitle" component="div">
            Rental Charts for zip, <strong>{analysisData.zip?.zipCode}</strong>
          </Typography>
          <Grid container spacing={2} >
            <Grid item xs={12} md={6} lg={6} >
              <Bar options={options(analysisData.zip, 'average')} data={data(analysisData.zip, 'average', domainCss)} ></Bar>
            </Grid>
            <Grid item xs={12} md={6} lg={6} >
              <Bar options={options(analysisData.zip, 'total')} data={data(analysisData.zip, 'total', domainCss)} ></Bar>
            </Grid>
          </Grid>
        </Box>
      </ErrorBoundary>
    </>}
  </>)
}

export default Graphy